import { InfoOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import Theme from "../../../utils/Theme";
import {
  buildChartData,
  buildMinorItems,
  mapExclusionsByLetter,
  MILES_STEP,
  INTRO_MINOR_DELAY,
  INTRO_BAR_STAGGER,
  INTRO_BAR_DURATION,
} from "../../../utils/warrantyChart.utils";

const BRAND_GREEN = Theme.palette.primary.main;
const BRAND_BLUE = "#0A2E5C";

const PORTRAIT_MOBILE_CHART_QUERY =
  "(orientation: portrait) and (max-width: 900px)";

const LANDSCAPE_MOBILE_CHART_QUERY =
  "(orientation: landscape) and (max-width: 900px)";

const usePortraitMobileChartLayout = () => {
  const [isPortraitMobile, setIsPortraitMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(PORTRAIT_MOBILE_CHART_QUERY).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(PORTRAIT_MOBILE_CHART_QUERY);
    const handleChange = () => setIsPortraitMobile(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return isPortraitMobile;
};

/** NEW: detects actual landscape orientation on narrow (phone) viewports. */
const useLandscapeMobileChartLayout = () => {
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(LANDSCAPE_MOBILE_CHART_QUERY).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(LANDSCAPE_MOBILE_CHART_QUERY);
    const handleChange = () => setIsLandscapeMobile(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return isLandscapeMobile;
};

/** Landscape popups inside the CSS-rotated chart container on portrait phones,
 *  and now also used directly for real landscape-mobile orientation. */
const portraitMobilePopupShellSx = {
  position: "absolute",
  inset: 0,
  zIndex: 1400,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: "rgba(0, 0, 0, 0.5)",
};

const portraitMobilePopupPaperSx = {
  bgcolor: "#fff",
  borderRadius: "10px",
  width: "min(80%, 560px)",
  maxWidth: "80%",
  maxHeight: "100%",
  minHeight: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  boxShadow: 24,
  "@media (orientation: landscape) and (max-width: 900px)": {
    maxHeight: "95%",
  },
};

const WarrantyChartPopup = ({
  open,
  onClose,
  title,
  children,
  isPortraitMobile,
  isLandscapeMobile,
}) => {
  if (!open) return null;

  // CHANGED: now renders the custom sized popup shell in EITHER
  // portrait-mobile OR landscape-mobile, instead of only portrait.
  if (isPortraitMobile || isLandscapeMobile) {
    return (
      <Box sx={portraitMobilePopupShellSx} onClick={onClose}>
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="warranty-chart-popup-title"
          onClick={(event) => event.stopPropagation()}
          sx={portraitMobilePopupPaperSx}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 0.45,

              flexShrink: 0,
            }}
          >
            <Typography
              id="warranty-chart-popup-title"
              sx={{ flex: 1, fontWeight: 800, fontSize: 16, pr: 1 }}
            >
              {title}
            </Typography>
            <IconButton onClick={onClose} size="small" aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ p: 2, pt: { xs: 1.5, sm: 1, md: 1 }, overflowY: "auto" }}>{children}</Box>
        </Box>
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6, fontWeight: 800, fontSize: 18 }}>
        {title}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
    </Dialog>
  );
};

/** Split newline / bullet text into display rows. Treats null/undefined/blank as "no items". */
const splitItems = (text) => {
  if (text === null || text === undefined) return [];
  return String(text)
    .split(/\r?\n|•|;/)
    .map((line) => line.replace(/^[\s•\-*]+/, "").trim())
    .filter(Boolean);
};

/** Shared striped multi-column table used by I + TIME popups. */
const WarrantyPopupTable = ({ columns, isSpanish, footer }) => {
  if (!columns?.length) return null;

  const noValuesLabel = isSpanish ? "Sin valores" : "No values";

  const headers = columns.map((col) =>
    isSpanish ? col.headerEs || col.headerEn || "" : col.headerEn || "",
  );
  const itemLists = columns.map((col) => {
    const list = splitItems(
      isSpanish ? col.itemsEs || col.itemsEn : col.itemsEn,
    );
    return list.length ? list : [noValuesLabel];
  });
  const maxRows = Math.max(0, ...itemLists.map((list) => list.length));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${columns.length}, 1fr)`,
          border: "1px solid #c8e6c9",
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        {headers.map((header, index) => (
          <Box
            key={`h-${index}`}
            sx={{
              bgcolor: "#c8e6c9",
              px: 1.25,
              py: 1,
              fontWeight: 700,
              fontSize: 14,
              color: "#000",
              borderRight:
                index < headers.length - 1 ? "1px solid #a5d6a7" : "none",
            }}
          >
            {header || "—"}
          </Box>
        ))}
        {Array.from({ length: maxRows }).map((_, rowIndex) =>
          itemLists.map((list, colIndex) => (
            <Box
              key={`c-${colIndex}-r-${rowIndex}`}
              sx={{
                px: 1.25,
                py: 0.75,
                fontSize: 14,
                color: "#000",
                fontStyle:
                  list[rowIndex] === noValuesLabel ? "italic" : "normal",
                bgcolor: rowIndex % 2 === 0 ? "#fff" : "#f1f8e9",
                borderRight:
                  colIndex < itemLists.length - 1
                    ? "1px solid #e0e0e0"
                    : "none",
                borderTop: "1px solid #e8f5e9",
                minHeight: 28,
              }}
            >
              {list[rowIndex] || ""}
            </Box>
          )),
        )}
      </Box>
      {footer ? (
        <Box
          sx={{
            bgcolor: "#e3f2fd",
            borderRadius: "6px",
            px: 1.5,
            py: 1.25,
          }}
        >
          <Typography
            variant="body2"
            
            sx={{ whiteSpace: "pre-wrap", fontSize: 14 , color: "#000" }}
          >
            {footer}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

/** Read-only popup for major coverage i-button (covered items table). */
const CoverageInfoDialog = ({
  open,
  onClose,
  title,
  subtitle,
  footer,
  items,
  isSpanish,
  isPortraitMobile,
  isLandscapeMobile,
}) => {
  const columns = (items || []).map((item) => ({
    headerEn: item.coverage_category_en,
    headerEs: item.coverage_category_es,
    itemsEn: item.coverage_items_en,
    itemsEs: item.coverage_items_es,
  }));
  const hasColumns = columns.some(
    (col) => col.headerEn || col.headerEs || col.itemsEn || col.itemsEs,
  );

  return (
    <WarrantyChartPopup
      open={open}
      onClose={onClose}
      title={title}
      isPortraitMobile={isPortraitMobile}
      isLandscapeMobile={isLandscapeMobile}
    >
      {subtitle ? (
        <Typography
          variant="body2"
         
          sx={{ 
  mt: {
    xs: -2,
    sm: -1,
    md: 0,
    lg: 0,
  },
 mb: 1.5, textAlign: "left", fontSize: 15, color: "#000" }}
        >
          {subtitle}
        </Typography>
      ) : null}
      {!hasColumns ? (
        <Typography variant="body2"  sx={{ fontSize: 15, color: "#000"  }}>
          {isSpanish
            ? "No hay información disponible."
            : "No information available."}
        </Typography>
      ) : (
        <WarrantyPopupTable
          columns={columns}
          isSpanish={isSpanish}
          footer={footer}
        />
      )}
    </WarrantyChartPopup>
  );
};

/** Read-only popup for TIME letter badges (uncovered items table). */
const TimeExclusionDialog = ({
  open,
  onClose,
  letter,
  exclusion,
  isSpanish,
  isPortraitMobile,
  isLandscapeMobile,
}) => {
  const title = isSpanish
    ? exclusion?.title_es ||
      exclusion?.title_en ||
      exclusion?.exclusion_category_es ||
      exclusion?.exclusion_category_en ||
      letter
    : exclusion?.title_en || exclusion?.exclusion_category_en || letter;

  const footer = isSpanish
    ? exclusion?.footer_es ||
      exclusion?.footer_en ||
      exclusion?.exclusion_items_es ||
      exclusion?.exclusion_items_en ||
      ""
    : exclusion?.footer_en || exclusion?.exclusion_items_en || "";

  const columns = (exclusion?.columns || []).map((col) => ({
    headerEn: col.column_header_en,
    headerEs: col.column_header_es,
    itemsEn: col.column_items_en,
    itemsEs: col.column_items_es,
  }));

  const hasColumns = columns.some(
    (col) => col.headerEn || col.headerEs || col.itemsEn || col.itemsEs,
  );

  const legacyWhatToKnow = isSpanish
    ? exclusion?.exclusion_category_es || exclusion?.exclusion_category_en
    : exclusion?.exclusion_category_en;
  const legacyDescription = isSpanish
    ? exclusion?.exclusion_items_es || exclusion?.exclusion_items_en
    : exclusion?.exclusion_items_en;
  const useLegacy =
    !hasColumns &&
    !exclusion?.title_en &&
    !exclusion?.title_es &&
    Boolean(legacyWhatToKnow || legacyDescription);

  return (
    <WarrantyChartPopup
      open={open}
      onClose={onClose}
      title={title}
      isPortraitMobile={isPortraitMobile}
      isLandscapeMobile={isLandscapeMobile}
    >
      <Box sx={{   mt: {
    xs: -1.5,
    sm: -1,
    md: 0,
    lg: 1,
  }, }}>
        {!hasColumns && !useLegacy ? (
          <Typography variant="body2"  sx={{ fontSize: 15, color: "#000"  }}>
            {isSpanish
              ? "No hay información disponible."
              : "No information available."}
          </Typography>
        ) : useLegacy ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {legacyWhatToKnow ? (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                sx={{ color: "#000" }}
                  display="block"
                  mb={0.5}
                  sx={{ fontSize: 13 }}
                >
                  {isSpanish ? "Qué Saber" : "What to Know"}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontSize: 15, color: "#000"  }}>
                  {legacyWhatToKnow}
                </Typography>
              </Box>
            ) : null}
            {legacyDescription ? (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                 sx={{ color: "#000" }}
                  display="block"
                  mb={0.5}
                >
                  {isSpanish ? "Descripción" : "Description"}
                </Typography>
                <Typography
                  variant="body2"
                  
                  sx={{ whiteSpace: "pre-wrap", fontSize: 15 , color: "#000" }}
                >
                  {legacyDescription}
                </Typography>
              </Box>
            ) : null}
          </Box>
        ) : (
          <WarrantyPopupTable
            columns={columns}
            isSpanish={isSpanish}
            footer={footer}
          />
        )}
      </Box>
    </WarrantyChartPopup>
  );
};

const StepperControl = ({
  label,
  value,
  onDecrement,
  onIncrement,
  min,
  max,
  displayValue,
  disabled = false,
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      alignItems: "center",
    }}
  >
    <Typography
      variant="caption"
      fontWeight={600}
      textAlign="center"
      sx={{ color: BRAND_GREEN }}
    >
      {label}
    </Typography>
    <Typography
      variant="body1"
      fontWeight={700}
      sx={{
        color: BRAND_GREEN,
        minWidth: 56,
        textAlign: "center",
        lineHeight: 1.2,
      }}
    >
      {displayValue ?? value}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <IconButton
        size="small"
        onClick={onDecrement}
        disabled={disabled || value <= min}
        sx={{
          width: {
            xs: 45,
            sm: 48,
            md: 60,
          },
          height: {
            xs: 45,
            sm: 48,
            md: 60,
          },
          border: `2px solid ${BRAND_GREEN}`,
          color: BRAND_GREEN,
          borderRadius: "50%",
          "&:disabled": { borderColor: "#ccc", color: "#ccc" },
        }}
      >
        <RemoveIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <IconButton
        size="small"
        onClick={onIncrement}
        disabled={disabled || value >= max}
        sx={{
          width: {
            xs: 45,
            sm: 48,
            md: 60,
          },
          height: {
            xs: 45,
            sm: 48,
            md: 60,
          },
          border: `2px solid ${BRAND_GREEN}`,
          color: BRAND_GREEN,
          borderRadius: "50%",
          "&:disabled": { borderColor: "#ccc", color: "#ccc" },
        }}
      >
        <AddIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  </Box>
);

const TimeBadge = ({ letter, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-label={`TIME ${letter}`}
    sx={{
      width: {
        xs: 45,
        sm: 45,
        md: 68,
      },
      height: {
        xs: 45,
        sm: 45,
        md: 68,
      },
      "@media (orientation: landscape) and (max-width: 900px)": {
        width: 40,
        height: 40,
        fontSize: 10,
      },
      borderRadius: "50%",
      display: "flex",
      border: "2px solid #9e9e9e",
      alignItems: "center",
      justifyContent: "center",
      color: "#616161",
      fontWeight: 700,
      fontSize: 12,
      background: "#fff",
      cursor: "pointer",
      p: 0,
      "&:hover": {
        borderColor: BRAND_GREEN,
        color: BRAND_GREEN,
      },
    }}
  >
    {letter}
  </Box>
);

const WarrantyChartView = ({
  coverages = [],
  exclusions = [],
  annualMiles,
  yearsOwned,
  onAnnualMilesChange,
  onYearsOwnedChange,
  isSpanish = false,
  readOnly = false,
  animate = true,
  compact = false,
  savedItems = null,
  headerAction = null,
  sx = {},
}) => {
  const isPortraitMobile = usePortraitMobileChartLayout();
  const isLandscapeMobile = useLandscapeMobileChartLayout();
  const [visibleMinorCount, setVisibleMinorCount] = useState(0);
  const [visibleBarCount, setVisibleBarCount] = useState(0);
  const [introDone, setIntroDone] = useState(!animate);
  const [infoDialog, setInfoDialog] = useState(null); // { title, items }
  const [timeDialog, setTimeDialog] = useState(null); // { letter, exclusion }

  const exclusionsByLetter = useMemo(
    () => mapExclusionsByLetter(exclusions),
    [exclusions],
  );

  const openTimePopup = (letter) => {
    setTimeDialog({
      letter,
      exclusion: exclusionsByLetter[letter] || null,
    });
  };

  const minorItems = useMemo(
    () => buildMinorItems(coverages, annualMiles, yearsOwned, isSpanish),
    [coverages, annualMiles, yearsOwned, isSpanish],
  );

  const chartData = useMemo(
    () =>
      buildChartData(coverages, annualMiles, yearsOwned, isSpanish, savedItems),
    [coverages, annualMiles, yearsOwned, isSpanish, savedItems],
  );

  const milesStepperEnabled =
    !readOnly && introDone && typeof onAnnualMilesChange === "function";
  const yearsStepperEnabled =
    !readOnly && introDone && typeof onYearsOwnedChange === "function";

  // Progressive intro: each minor, then each major bar — 300ms apart.
  useEffect(() => {
    const majorCount = chartData.length;
    const minorCount = minorItems.length;

    if (!animate) {
      setVisibleMinorCount(minorCount);
      setVisibleBarCount(majorCount);
      setIntroDone(true);
      return undefined;
    }

    let cancelled = false;
    const timers = [];

    setVisibleMinorCount(0);
    setVisibleBarCount(0);
    setIntroDone(false);

    if (majorCount === 0 && minorCount === 0) {
      setIntroDone(true);
      return undefined;
    }

    for (let i = 0; i < minorCount; i += 1) {
      timers.push(
        setTimeout(
          () => {
            if (!cancelled) setVisibleMinorCount(i + 1);
          },
          INTRO_MINOR_DELAY + i * INTRO_BAR_STAGGER,
        ),
      );
    }

    const barStart = INTRO_MINOR_DELAY + minorCount * INTRO_BAR_STAGGER;

    if (majorCount === 0) {
      timers.push(
        setTimeout(() => {
          if (!cancelled) setIntroDone(true);
        }, barStart),
      );
    } else {
      for (let i = 0; i < majorCount; i += 1) {
        timers.push(
          setTimeout(
            () => {
              if (!cancelled) setVisibleBarCount(i + 1);
            },
            barStart + i * INTRO_BAR_STAGGER,
          ),
        );
      }
      timers.push(
        setTimeout(
          () => {
            if (!cancelled) setIntroDone(true);
          },
          barStart +
            (majorCount - 1) * INTRO_BAR_STAGGER +
            INTRO_BAR_DURATION +
            80,
        ),
      );
    }

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [animate, chartData.length, minorItems.length]);

  const animatedChartData = useMemo(
    () =>
      chartData.map((row, i) => {
        const revealed = introDone || i < visibleBarCount;
        return {
          ...row,
          covered: revealed ? row.covered : 0,
          uncovered: revealed ? row.uncovered : 0,
          revealed,
        };
      }),
    [chartData, introDone, visibleBarCount],
  );

  const totalMonths = yearsOwned * 12;

  return (
    <Box
      sx={{
        height: compact ? "auto" : "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        p: compact ? 0 : { xs: 1.5, sm: 2, md: 2.5 },
        boxSizing: "border-box",
        overflow: compact ? "visible" : "hidden",
        position: isPortraitMobile ? "relative" : undefined,
        ...sx,
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: compact ? "auto" : "100%",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1, sm: 1.5 },
          minHeight: compact ? "auto" : 0,
        }}
      >
        {/* Header + action */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              background: "#0A2E5C",
              backgroundColor: "#0A2E5C",
              borderRadius: "4px",
              px: 2,
              py: {
                xs: 0.2, // Mobile
                sm: 0.5, // Tablet
                md: 1, // Desktop
              },
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="#fff"
              textAlign="center"
              sx={{ fontSize: { xs: 14, sm: 16 } }}
            >
              {isSpanish
                ? "Su Línea de Tiempo de Cobertura"
                : "Your Coverage Timeline"}
            </Typography>
          </Box>
          {headerAction}
        </Box>

        {/* Controls + Chart + TIME */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: { xs: 1.5, sm: 2.5 },
            alignItems: compact ? "flex-start" : "stretch",
            justifyContent: "stretch",
            flex: compact ? "none" : 1,
            minHeight: compact ? "auto" : 0,
            width: "100%",
          }}
        >
          {!readOnly ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                flexShrink: 0,
                width: { xs: 88, sm: 110 },
                justifyContent: "center",
                mt: -10,
              }}
            >
              <Box
                sx={{
                  mt: {
                    xs: 3,
                    md: -6, // Increase to 3 or 4 if you want it lower
                  },
                  "@media (orientation: landscape) and (max-width: 900px)": {
                    mt: 8,
                  },
                }}
              >
                <StepperControl
                  label={isSpanish ? "Millas por año" : "Miles per Year"}
                  value={annualMiles}
                  displayValue={Number(annualMiles).toLocaleString()}
                  onDecrement={() =>
                    onAnnualMilesChange?.(
                      Math.max(MILES_STEP, annualMiles - MILES_STEP),
                    )
                  }
                  onIncrement={() =>
                    onAnnualMilesChange?.(
                      Math.min(100000, annualMiles + MILES_STEP),
                    )
                  }
                  min={MILES_STEP}
                  max={100000}
                  disabled={!milesStepperEnabled}
                />
              </Box>
              <StepperControl
                label={isSpanish ? "Años de posesión" : "Years You'll Own"}
                value={yearsOwned}
                onDecrement={() =>
                  onYearsOwnedChange?.(Math.max(1, yearsOwned - 1))
                }
                onIncrement={() =>
                  onYearsOwnedChange?.(Math.min(20, yearsOwned + 1))
                }
                min={1}
                max={20}
                disabled={!yearsStepperEnabled}
              />
            </Box>
          ) : null}

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: compact ? "auto" : 0,
              display: "flex",
              flexDirection: "row",
              gap: 1,
            }}
          >
            {chartData.length === 0 ? (
              <Typography
                variant="body2"
               
                sx={{
                  py: 4,
                  textAlign: "center",
                  width: "100%",
                  alignSelf: "center",
                   color: "#000" 
                }}
              >
                {isSpanish
                  ? "No hay coberturas configuradas para este tipo de vehículo."
                  : "No coverages configured for this vehicle type."}
              </Typography>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  width: {
                    xs: "50%",
                    md: "50%", // Reduce width on desktop
                  },
                  mx: "auto", // Center the chart
                  minWidth: 0,
                  minHeight: compact ? "auto" : 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: compact ? "flex-start" : "space-evenly",
                  gap: 0.25,
                  py: 0.5,
                }}
              >
                {animatedChartData.map((row) => {
                  const total = Math.max(row.total || totalMonths, 1);
                  const coveredPct = row.revealed
                    ? (row.covered / total) * 100
                    : 0;
                  const uncoveredPct = row.revealed
                    ? (row.uncovered / total) * 100
                    : 0;

                  return (
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-label={`${row.name} info`}
                      onClick={() =>
                        setInfoDialog({
                          title: row.infoTitle || row.name,
                          subtitle: row.infoSubtitle || "",
                          footer: row.infoFooter || "",
                          items: row.infoItems || [],
                        })
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setInfoDialog({
                            title: row.infoTitle || row.name,
                            subtitle: row.infoSubtitle || "",
                            footer: row.infoFooter || "",
                            items: row.infoItems || [],
                          });
                        }
                      }}
                      sx={{
                        position: "relative",
                        height: {
                          xs: 65,
                          sm: 65,
                          md: 126,
                        },
                        width: "100%",
                        opacity: row.revealed ? 1 : 0,
                        transition: "opacity 280ms ease",
                        borderRadius: "4px",
                        overflow: "hidden",
                        display: "flex",
                        backgroundColor: "#eceff1",
                        cursor: "pointer",
                        "&:hover": {
                          filter: "brightness(0.97)",
                        },
                        "&:focus-visible": {
                          outline: `2px solid ${BRAND_GREEN}`,
                          outlineOffset: "2px",
                        },
                      }}
                    >
                      {/* Covered (green) */}
                      <Box
                        sx={{
                          width: `${coveredPct}%`,
                          height: "100%",
                          backgroundColor: "#2e7d32",
                          transition: introDone
                            ? "width 400ms ease-out"
                            : `width ${INTRO_BAR_DURATION}ms ease-out`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          pr: coveredPct > 18 ? 1 : 0.25,
                          minWidth: coveredPct > 0 ? 4 : 0,
                          position: "relative",
                        }}
                      >
                        {row.covered > 0 && coveredPct > 12 ? (
                          <Typography
                            sx={{
                              color: "#fff",
                              fontSize: 11,
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              zIndex: 1,
                            }}
                          >
                            {row.covered} mo
                          </Typography>
                        ) : null}
                      </Box>

                      {/* Uncovered (red) */}
                      <Box
                        sx={{
                          width: `${uncoveredPct}%`,
                          height: "100%",
                          backgroundColor: "#c62828",
                          transition: introDone
                            ? "width 400ms ease-out"
                            : `width ${INTRO_BAR_DURATION}ms ease-out`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: uncoveredPct > 0 ? 4 : 0,
                        }}
                      >
                        {row.uncovered > 0 && uncoveredPct > 10 ? (
                          <Typography
                            sx={{
                              color: "#fff",
                              fontSize: 11,
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.uncovered} mo
                          </Typography>
                        ) : null}
                      </Box>

                      {/* i + label ON TOP of the left end of the bar */}
                      <Box
                        sx={{
                          position: "absolute",
                          left: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          zIndex: 2,
                          pointerEvents: "none",
                        }}
                      >
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            backgroundColor: "#37474f",
                            color: "#fff",
                            display: "none",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <InfoOutlined sx={{ fontSize: 14 }} />
                        </Box>

                        <Box
                          sx={{
                            border: "1px solid #cfd8dc",
                            borderRadius: "3px",
                            width: {
                              xs: 125,
                              sm: 125,
                              md: 130,
                            },
                            py: 0.4,
                            px: 1,
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#37474f",
                            background: "#fff",
                            textAlign: "start",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                          }}
                        >
                          {row.name}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flexShrink: 0,
              mt: {
                xs: 3,
                sm: 3,
                md: 7,
              },
              
              width: { xs: 40, sm: 70 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 10, sm: 11 },
                fontWeight: 800,
                color: "#424242",
                textAlign: "center",
                lineHeight: 1.1,
                mt: {
                  xs: -2.5,
                  sm: -2.5,
                  md: -1,
                },
                mb: {
                  xs: 5,
                  sm: 5,
                  md: 10,
                },
                "@media (orientation: landscape) and (max-width: 900px)": {
                  mb: 0.5,
                },
              }}
            >
            
              <br />
              Not
              <br />
              Covered
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: {
                  xs: 2.3,
                  sm: 2,
                  md: 7,
                },
                "@media (orientation: landscape) and (max-width: 900px)": {
                  gap: 0.75,
                },
              }}
            >
              {["T", "I", "M", "E"].map((letter) => (
                <TimeBadge
                  key={letter}
                  letter={letter}
                  onClick={() => openTimePopup(letter)}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <CoverageInfoDialog
        open={Boolean(infoDialog)}
        onClose={() => setInfoDialog(null)}
        title={infoDialog?.title || ""}
        subtitle={infoDialog?.subtitle || ""}
        footer={infoDialog?.footer || ""}
        items={infoDialog?.items || []}
        isSpanish={isSpanish}
        isPortraitMobile={isPortraitMobile}
        isLandscapeMobile={isLandscapeMobile}
      />

      <TimeExclusionDialog
        open={Boolean(timeDialog)}
        onClose={() => setTimeDialog(null)}
        letter={timeDialog?.letter || ""}
        exclusion={timeDialog?.exclusion}
        isSpanish={isSpanish}
        isPortraitMobile={isPortraitMobile}
        isLandscapeMobile={isLandscapeMobile}
      />
    </Box>
  );
};

export default WarrantyChartView;